import { BodyLarge, Flex, Headline3 } from 'components/Elements';
import strings from 'strings/strings.json';
import React from 'react';
import { animated, useSpring } from '@react-spring/web';
import styled from 'styled-components';
import { sizes } from 'theme';
import ArrowDownIcon from 'assets/icons/controls/ArrowDownIcon';
import ArrowUpIcon from 'assets/icons/controls/ArrowUpIcon';
import { ExpandableContentIconButton } from './buttons/ExpandableContentIconButton';
import { ExpandableContentButtonFullwidth } from './buttons/ExpandableContentButtonFullwidth';

type ExpandableTextContainerProps = {
    headline: string | JSX.Element;
    content: string | JSX.Element;
    closedHeightInPx: number;
    expandedByDefault?: boolean;
    bigButton?: boolean;
};

export default function ExpandableContentComponent({
    headline,
    content,
    closedHeightInPx,
    expandedByDefault,
    bigButton,
}: ExpandableTextContainerProps): JSX.Element {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isExpandable, setIsExpandable] = React.useState(false);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentId = React.useId();

    const { height } = useSpring({
        height: isExpanded ? `${containerRef.current?.scrollHeight}px` : `${closedHeightInPx}px`,
    });

    React.useEffect(() => {
        if (containerRef.current) {
            const isContentLongerThanClosedHeight = containerRef.current.scrollHeight > closedHeightInPx;
            setIsExpandable(isContentLongerThanClosedHeight);
        }
        if (expandedByDefault) setIsExpanded(true);
    }, [closedHeightInPx, expandedByDefault]);

    const contentAsElement =
        typeof content === 'string' ? <DescriptionText>{content}</DescriptionText> : <>{content}</>;

    let parsedHeadline = <></>;
    if (typeof headline === 'string') parsedHeadline = <Headline3>{headline}</Headline3>;
    else if (React.isValidElement(headline)) parsedHeadline = headline;

    if (bigButton) {
        return (
            <>
                <ExpandableContentButtonFullwidth
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <HeadlineFlex>
                        {parsedHeadline}
                        {isExpandable && (isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />)}
                    </HeadlineFlex>
                </ExpandableContentButtonFullwidth>

                <ExpandableContent
                    id={contentId}
                    ref={containerRef}
                    fadeheight={isExpanded}
                    fadecontent={isExpandable}
                    style={{ height: isExpandable ? height : 'auto', overflow: 'hidden' }}
                >
                    {contentAsElement}
                </ExpandableContent>
            </>
        );
    }

    return (
        <Container>
            <HeadlineFlex>
                {parsedHeadline}
                {isExpandable && (
                    <ExpandableContentIconButton
                        aria-expanded={isExpanded}
                        aria-controls={contentId}
                        aria={
                            isExpanded
                                ? strings.expandableContent.collapseContentAria
                                : strings.expandableContent.expandContentAria
                        }
                        onClick={() => setIsExpanded(!isExpanded)}
                        icon={isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                    />
                )}
            </HeadlineFlex>
            <ExpandableContent
                id={contentId}
                ref={containerRef}
                fadeheight={isExpanded}
                fadecontent={isExpandable}
                style={{ height: isExpandable ? height : 'auto', overflow: 'hidden' }}
            >
                {contentAsElement}
            </ExpandableContent>
        </Container>
    );
}

const ExpandableContent = styled(animated.div)<{ fadeheight: boolean; fadecontent: boolean }>`
    position: relative;

    &:before {
        content: '';
        position: absolute;
        width: 100%;
        top: 0;
        left: 0;
        z-index: 100;
        height: ${({ fadeheight }) => {
            return fadeheight ? 0 : '100%';
        }};
        background-image: ${({ fadecontent }) => {
            return fadecontent ? 'linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))' : 'none';
        }};
    }
`;

const DescriptionText = styled(BodyLarge)`
    white-space: pre-wrap;
    overflow-wrap: break-word;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingSm};
`;

const HeadlineFlex = styled(Flex)`
    justify-content: space-between;
`;
